import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CloudUpload, Download, Trash } from "lucide-react";
import { Button } from "@speakai/ui/button";
import { ConfirmDialog } from "@speakai/ui/confirm-dialog";
import { formatTimestamp } from "@speakai/common/src/utils/format";
import { formatDurationHuman } from "@/lib/utils/format";
import { RECORD_MODES } from "@/features/recorder/constants";
import type { SavedTake } from "@/features/recorder/types";

interface RecoverTakeBannerProps {
  takes: SavedTake[];
  /** The take an action is running for; its buttons are disabled meanwhile. */
  busyTakeId: string | null;
  onUpload: (take: SavedTake) => void;
  onDownload: (take: SavedTake) => void;
  onDiscard: (take: SavedTake) => void;
}

export function RecoverTakeBanner({ takes, busyTakeId, onUpload, onDownload, onDiscard }: RecoverTakeBannerProps) {
  const { t } = useTranslation();
  const [takeToDiscard, setTakeToDiscard] = useState<SavedTake | null>(null);

  if (takes.length === 0) return null;

  return (
    <div className="flex flex-col gap-3" data-testid="recover-take-banner">
      {takes.map((take) => {
        const isBusy = busyTakeId === take.id;
        const modeOption = RECORD_MODES.find((option) => option.mode === take.mode) ?? RECORD_MODES[0];
        const ModeIcon = modeOption.icon;
        return (
          <div
            key={take.id}
            className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5"
            data-testid="recover-take-row"
          >
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <ModeIcon className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t("UPLOAD.RECORD.RECOVER_TITLE", "Unsaved recording found")}
                  <span className="sr-only"> · {t(modeOption.labelKey, modeOption.label)}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "UPLOAD.RECORD.RECOVER_DESC",
                    "{{duration}} · {{date}} · Kept in this browser for 7 days",
                    { duration: formatDurationHuman(take.durationSec), date: formatTimestamp(take.startedAt) },
                  )}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
              <Button
                size="sm"
                className="gap-2"
                disabled={isBusy}
                onClick={() => onUpload(take)}
                data-testid="recover-take-upload"
              >
                <CloudUpload className="h-4 w-4" aria-hidden="true" />
                {t("ACTION.UPLOAD", "Upload")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                disabled={isBusy}
                onClick={() => onDownload(take)}
                data-testid="recover-take-download"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {t("ACTION.DOWNLOAD", "Download")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="gap-2"
                disabled={isBusy}
                onClick={() => setTakeToDiscard(take)}
                data-testid="recover-take-discard"
              >
                <Trash className="h-4 w-4" aria-hidden="true" />
                {t("ACTION.DISCARD", "Discard")}
              </Button>
            </div>
          </div>
        );
      })}

      <ConfirmDialog
        open={takeToDiscard !== null}
        onClose={() => setTakeToDiscard(null)}
        onConfirm={() => {
          if (takeToDiscard) onDiscard(takeToDiscard);
          setTakeToDiscard(null);
        }}
        variant="danger"
        title={t("RECORD.DISCARD_RECORDING_TITLE", "Discard this recording?")}
        description={t(
          "UPLOAD.RECORD.RECOVER_DISCARD_DESC",
          "This deletes the {{duration}} recording from this browser. You can't undo this.",
          { duration: formatDurationHuman(takeToDiscard?.durationSec ?? 0) },
        )}
        confirmLabel={t("ACTION.DISCARD", "Discard")}
        cancelLabel={t("ACTION.CANCEL", "Cancel")}
      />
    </div>
  );
}
